import React from 'react';

import { Alert, Box, Button, Checkbox, Divider, FormControlLabel, Stack, Typography } from '@mui/material';

import type { RoomRateRoom } from './utils';

interface Props {
  createdRatePlan: App.RatePlan | null;
  disabled: boolean;
  onClearRooms: () => void;
  onSelectAllRooms: () => void;
  onToggleRoom: (roomTypeId: string) => void;
  readOnly?: boolean;
  rooms: Array<RoomRateRoom>;
  selectedRoomTypeIds: Array<string>;
}

export default function RoomRatesStep({
  createdRatePlan,
  disabled,
  onClearRooms,
  onSelectAllRooms,
  onToggleRoom,
  readOnly = false,
  rooms,
  selectedRoomTypeIds,
}: Props) {
  const selectableRooms = rooms.filter((room) => !room.hasRatePlan);
  const selectedRoomCount = selectedRoomTypeIds.length;
  const allSelectableRoomsSelected = selectedRoomCount === selectableRooms.length;

  if (!createdRatePlan?.id) {
    return <Alert severity="error">Create a Flash rate plan before applying room rates.</Alert>;
  }

  if (rooms.length === 0) {
    return <Alert severity="warning">No rooms are available for this property.</Alert>;
  }

  return (
    <Stack spacing={2}>
      <Alert severity="info">
        {readOnly
          ? `${createdRatePlan.name || 'The new Flash rate plan'} has been applied to the selected rooms.`
          : `Select the rooms that should use ${createdRatePlan.name || 'the new Flash rate plan'}.`}
      </Alert>
      <Box
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: 'background.paper',
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
          sx={{ px: 2, py: 1.25, bgcolor: 'grey.50' }}
        >
          <Stack spacing={0.25}>
            <Typography variant="subtitle2">Rooms</Typography>
            <Typography variant="caption" color="text.secondary">
              {selectedRoomCount} of {selectableRooms.length} rooms selected
            </Typography>
          </Stack>
          {!readOnly && (
            <Stack direction="row" spacing={1}>
              <Button
                variant="text"
                size="small"
                onClick={onSelectAllRooms}
                disabled={disabled || selectableRooms.length === 0 || allSelectableRoomsSelected}
              >
                Select all rooms
              </Button>
              <Button variant="text" size="small" onClick={onClearRooms} disabled={disabled || selectedRoomCount === 0}>
                Clear
              </Button>
            </Stack>
          )}
        </Stack>
        <Divider />
        {rooms.map((room) => (
          <Box key={room.id}>
            <FormControlLabel
              sx={{
                m: 0,
                width: '100%',
                minHeight: 48,
                px: 1.5,
                py: 0.25,
                borderLeft: 3,
                borderColor: selectedRoomTypeIds.includes(room.id) ? 'primary.main' : 'transparent',
                bgcolor: 'background.paper',
                '&:hover': {
                  bgcolor: room.hasRatePlan || disabled ? undefined : 'action.hover',
                },
                '& .MuiCheckbox-root': {
                  p: 1,
                },
                '& .MuiFormControlLabel-label': {
                  width: '100%',
                },
              }}
              control={
                <Checkbox
                  checked={room.hasRatePlan || selectedRoomTypeIds.includes(room.id)}
                  disabled={disabled || readOnly || room.hasRatePlan}
                  onChange={() => onToggleRoom(room.id)}
                  sx={{ mr: 1.25 }}
                />
              }
              label={
                <Stack>
                  <Typography variant="body2">{room.name}</Typography>
                  {room.hasRatePlan && (
                    <Typography variant="caption" color="text.secondary">
                      Rate plan already applied
                    </Typography>
                  )}
                </Stack>
              }
            />
            <Divider />
          </Box>
        ))}
      </Box>
    </Stack>
  );
}
